/**
 * Typed API failures.
 *
 * Call sites render `err.message` straight into the UI, so an `ApiError`'s
 * message is always human-facing and the technical detail lives on `.detail`
 * for the console. Ported from web-v2 `src/lib/api-errors.ts` unchanged in
 * behaviour; `HttpError` is new and replaces web-v2's ad-hoc
 * `Error & { status }` for ordinary 4xx/5xx answers.
 */

export type ApiFailureKind =
  /** The browser reports no network at all. */
  | 'offline'
  /** The request never got an answer: DNS, refused connection, CORS-less ALB error. */
  | 'unreachable'
  /** A gateway answered but no healthy app instance did (502/503/504). */
  | 'unavailable'
  /** The app answered with a 5xx. */
  | 'server'
  /** The app is up but turned a read away under load (429 from an admission gate); waiting and retrying is the answer. */
  | 'busy'
  /** Ordinary 4xx / app-level error. */
  | 'request';

export interface ApiErrorInit {
  kind: ApiFailureKind;
  status?: number;
  method: string;
  url: string;
  detail: string;
  retriable: boolean;
  /** Parsed error body, when the server sent one. */
  body?: unknown;
}

const FRIENDLY_MESSAGE: Record<ApiFailureKind, string> = {
  offline: "You're offline. Reconnect and try again — nothing was lost.",
  unreachable: "Can't reach Dreamwell right now — it may be updating. Nothing was lost; try again in a moment.",
  unavailable: 'Dreamwell is updating. This usually clears within a minute — try again shortly.',
  server: 'Something went wrong on our side. Try again in a moment.',
  busy: 'Dreamwell is busy right now. Try again in a moment.',
  request: 'That request could not be completed.',
};

export class ApiError extends Error {
  readonly kind: ApiFailureKind;
  readonly status?: number;
  readonly method: string;
  readonly url: string;
  /** Technical description, for logs and bug reports — never rendered raw. */
  readonly detail: string;
  /** True when waiting and retrying is the sensible response. */
  readonly retriable: boolean;
  readonly body?: unknown;

  constructor(init: ApiErrorInit, message?: string) {
    super(message || FRIENDLY_MESSAGE[init.kind]);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.method = init.method;
    this.url = init.url;
    this.detail = init.detail;
    this.retriable = init.retriable;
    this.body = init.body;
  }
}

/** Statuses that mean "the platform is between states", not "your request was wrong". */
export const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * "Too many at once — come back shortly." dreamwell-api's admission gates
 * (`infrastructure/process/admissionGate.ts`) answer it when a heavy read
 * (`/managed/artifacts`, `/managed/brief-suggestions`, workspace state) waited
 * out its turn. The API is healthy, so it never counts as an outage.
 */
export const BUSY_STATUS = 429;

/** A read the API turned away under load, after the client's own retries. */
export function isBusyError(err: unknown): boolean {
  return err instanceof ApiError && err.kind === 'busy';
}

/**
 * A rejected `fetch` is a TypeError with no detail — the browser hides whether
 * it was DNS, a refused connection, or a response the CORS check threw away
 * (which is exactly what an ALB 503 with no healthy targets looks like from
 * JavaScript). `navigator.onLine` is the only extra signal available.
 */
export function classifyTransportFailure(): 'offline' | 'unreachable' {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  return 'unreachable';
}

/** True when the failure is about reaching the backend, not about the request itself. */
export function isConnectivityError(err: unknown): boolean {
  return err instanceof ApiError && (err.kind === 'offline' || err.kind === 'unreachable' || err.kind === 'unavailable');
}

/** True when retrying the same call later has a real chance of succeeding. */
export function isRetriableError(err: unknown): boolean {
  if (err instanceof ApiError) return err.retriable;
  return err instanceof TypeError;
}

/** 404: what was asked for isn't there (gone, or another team's) — as opposed to a read that failed. */
export function isNotFoundError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/** 401/403: the session is gone or the user may not do this. Never worth retrying. */
export function isAuthError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

/** The message to show a person for any thrown value. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Try again.'): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
